import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../useApi';
import type { JobResult, MergeDetail, WritePolicy } from '../api';
import { ErrorNotice } from '../components/ErrorNotice';
import { fieldLabel, fieldTitle, isAttribute, isNested } from '../fieldLabels';
import { awaitJob, type JobProgress } from '../awaitJob';
import { canConfirm } from '../writeGate';
import { WriteBlockedNotice } from '../components/WriteBlockedNotice.js';

// Merging two records into one.
//
// The screen is arranged around the decision that actually matters. A merge
// cannot combine two records — Acumatica has no way to move posted documents
// from one business account to another — so one record keeps its history and
// the other is retired. Choosing the wrong one strands every invoice and
// payment under a record nobody is meant to use again.
//
// Which is why the document counts come first, before any field. Everything
// below them is a smaller decision: the survivor can take any value from the
// record being retired, and those are one click each.

type Side = 'A' | 'B';

/** Fields nobody chooses: identity, and values Acumatica maintains itself. */
const NOT_CHOOSABLE = ['id', 'LastModifiedDateTime'];

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

export function MergePage() {
  const { tenantId, entity, candidateId } = useParams<{
    tenantId: string;
    entity: string;
    candidateId: string;
  }>();
  const api = useApi();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<MergeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [survivorSide, setSurvivorSide] = useState<Side | null>(null);
  const [picks, setPicks] = useState<Record<string, Side>>({});
  const [submitting, setSubmitting] = useState(false);
  /** Null until loaded, and null again if the load failed — see writeGate. */
  const [policy, setPolicy] = useState<WritePolicy | null>(null);
  /** Synchronous twin of `submitting` — see merge() for why state is not enough. */
  const submittingRef = useRef(false);
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const [result, setResult] = useState<(JobResult & { survivorId: string; loserId: string }) | null>(
    null
  );

  const load = useCallback(async () => {
    if (!api || !tenantId || !candidateId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<MergeDetail>(
        `/tenants/${tenantId}/duplicates/${candidateId}`
      );
      setDetail(data);
      // Pre-select the record with more history. It is the right answer almost
      // every time, and leaving it unselected would invite whoever is in a
      // hurry to pick the one on the left.
      setSurvivorSide(data.historyB.total > data.historyA.total ? 'B' : 'A');
      setPicks({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the pair.');
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, candidateId]);

  useEffect(() => {
    if (!api || !tenantId) return;
    api
      .get<WritePolicy>(`/tenants/${tenantId}/write-policy`)
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [api, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    if (!detail) return [];
    const names = [
      ...detail.fields,
      ...Object.keys({ ...detail.recordA, ...detail.recordB }).filter(isAttribute),
    ].filter((name) => !NOT_CHOOSABLE.includes(name));

    return names.map((name) => {
      const a = text(detail.recordA?.[name]);
      const b = text(detail.recordB?.[name]);
      return { name, a, b, differs: a !== b, isKey: name === detail.keyField };
    });
  }, [detail]);

  if (loading) return <p className="muted">Loading…</p>;

  if (!detail) {
    return (
      <>
        <div className="page-header">
          <h1>Merge</h1>
        </div>
        <ErrorNotice message={error} onRetry={() => void load()} />
      </>
    );
  }

  const survivor = survivorSide === 'B' ? detail.candidate.recordIdB : detail.candidate.recordIdA;
  const loser = survivorSide === 'B' ? detail.candidate.recordIdA : detail.candidate.recordIdB;
  const survivorRecord = survivorSide === 'B' ? detail.recordB : detail.recordA;
  const survivorHistory = survivorSide === 'B' ? detail.historyB : detail.historyA;
  const loserHistory = survivorSide === 'B' ? detail.historyA : detail.historyB;

  /** Fields the survivor will take from the record being retired. */
  const carried = rows.filter(
    (row) => row.differs && !row.isKey && picks[row.name] !== undefined && picks[row.name] !== survivorSide
  );

  async function merge() {
    // ⚠ A REF, NOT THE `submitting` STATE — see confirmChanges in
    // EntityGridPage for the proof. This guard was cited as the version the
    // grid should copy, and it has the same hole: two clicks in one tick close
    // over the same pre-update `false` and both proceed, because React has not
    // re-rendered in between. On a merge that means two merge jobs for one
    // pair, and two of the licence's Acumatica seats.
    if (!api || !tenantId || !candidateId) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      // 202: the merge runs as a job, same write path and same undo log as any
      // other change set. Watched to completion below.
      const accepted = await api.post<JobResult & { survivorId: string; loserId: string }>(
        `/tenants/${tenantId}/duplicates/${candidateId}/merge`,
        {
          survivor,
          choices: carried.map((row) => ({
            fieldName: row.name,
            // What we showed the reviewer, so the server can refuse the merge
            // if somebody edited the record while they were deciding.
            beforeValue: text(survivorRecord?.[row.name]) || null,
            afterValue: (survivorSide === 'A' ? row.b : row.a) || null,
          })),
        }
      );

      const finished = await awaitJob(api, tenantId, accepted.job.jobId, setProgress);
      setResult({ ...finished, survivorId: accepted.survivorId, loserId: accepted.loserId });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The merge could not be completed.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      setProgress(null);
    }
  }

  function historyLine(history: MergeDetail['historyA']): string {
    if (history.counts.length === 0) return 'No document count available';
    if (history.total === 0) return 'No documents attached';
    return history.counts
      .filter((c) => c.count > 0)
      .map((c) => `${c.count}${c.atLeast ? '+' : ''} ${c.label}`)
      .join(' · ');
  }

  if (result) {
    const written = result.changes.filter((c) => c.writeStatus === 'written').length;
    const failed = result.changes.filter((c) => c.writeStatus === 'failed');

    return (
      <>
        <div className="page-header">
          <h1>{failed.length === 0 ? 'Merged' : 'Merge incomplete'}</h1>
        </div>
        <div className="card">
          {failed.length === 0 ? (
            <p>
              <strong className="mono">{result.survivorId}</strong> is the record to use.{' '}
              <strong className="mono">{result.loserId}</strong> is now inactive, and its
              history stays under it. Both records carry a note pointing at the other.
            </p>
          ) : (
            <div className="notice error">
              {written} of {result.changes.length} changes were written. The rest are listed
              below with the reason — nothing was rolled back, so review them before merging
              again.
            </div>
          )}

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Record</th>
                  <th>Field</th>
                  <th>Result</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {result.changes.map((change) => (
                  <tr key={`${change.recordId}:${change.fieldName}`}>
                    <td className="mono">{change.recordId}</td>
                    <td>{fieldLabel(change.fieldName)}</td>
                    <td>
                      <span className={`status-pill status-${change.writeStatus}`}>
                        {change.writeStatus}
                      </span>
                    </td>
                    <td className="wrap muted small">
                      {change.errorReason ??
                        `${change.beforeValue ?? '(empty)'} → ${change.afterValue ?? '(empty)'}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button
              type="button"
              onClick={() => navigate(`/tenants/${tenantId}/entities/${entity}/duplicates`)}
            >
              Back to duplicates
            </button>
            <Link className="button-link" to={`/tenants/${tenantId}/jobs`}>
              Job history
            </Link>
          </div>
          <p className="muted small">
            A merge is reversible. Undoing it from job history reactivates{' '}
            <span className="mono">{result.loserId}</span>, removes both notes, restores any
            field it carried over, and puts the pair back in the review queue.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <Link to={`/tenants/${tenantId}/entities/${entity}/duplicates`} className="muted small">
            ← Duplicates
          </Link>
          <h1>Merge {detail.candidate.entityType.toLowerCase()} records</h1>
        </div>
      </div>

      <ErrorNotice message={error} onRetry={() => void load()} />

      <div className="card">
        <h2 className="section-heading">1. Choose the record to keep</h2>
        <p className="muted small">
          Acumatica cannot move invoices, orders or payments from one record to another, so
          the record you keep is the one that keeps its history. The other stays in place,
          inactive, with its own history under it.
        </p>

        <div className="merge-choice">
          {(['A', 'B'] as const).map((side) => {
            const id = side === 'A' ? detail.candidate.recordIdA : detail.candidate.recordIdB;
            const history = side === 'A' ? detail.historyA : detail.historyB;
            const record = side === 'A' ? detail.recordA : detail.recordB;
            const other = side === 'A' ? detail.historyB : detail.historyA;
            return (
              <label
                key={side}
                className={survivorSide === side ? 'merge-card selected' : 'merge-card'}
              >
                <span className="merge-card-head">
                  <input
                    type="radio"
                    name="survivor"
                    checked={survivorSide === side}
                    onChange={() => setSurvivorSide(side)}
                  />
                  <strong className="mono">{id}</strong>
                  {survivorSide === side && <span className="keep-tag">Keeping</span>}
                </span>
                <span className="merge-history">
                  <strong>
                    {history.total}
                    {history.approximate ? '+' : ''} document
                    {history.total === 1 ? '' : 's'}
                  </strong>
                  <span className="muted small">{historyLine(history)}</span>
                  {history.total > other.total && history.total > 0 && (
                    <span className="small">More history than the other record.</span>
                  )}
                </span>
                <span className="muted small">
                  {text(record?.[detail.fields[1] ?? '']) || '—'}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="card">
        <h2 className="section-heading">2. Choose the values to keep</h2>
        <p className="muted small">
          Only fields where the two records disagree need a decision. The record being kept
          starts with its own values; click a value from the other record to carry it over.
        </p>

        <div className="table-scroll">
          <table className="data-table merge-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>{detail.candidate.recordIdA}</th>
                <th>{detail.candidate.recordIdB}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                // The key is identity, not a value: changing it would strand
                // the history the whole screen is about. Shown for context,
                // never as a choice.
                const chosen = row.isKey ? null : (picks[row.name] ?? survivorSide);
                return (
                  <tr key={row.name} className={row.differs ? undefined : 'muted'}>
                    <td title={fieldTitle(row.name)}>
                      {fieldLabel(row.name)}
                      {isAttribute(row.name) && <span className="attribute-tag">attribute</span>}
                      {isNested(row.name) && <span className="nested-tag">contact</span>}
                    </td>
                    {(['A', 'B'] as const).map((side) => {
                      const value = side === 'A' ? row.a : row.b;
                      if (!row.differs || row.isKey) {
                        return (
                          <td key={side} className="merge-value">
                            {value || <em className="muted">empty</em>}
                          </td>
                        );
                      }
                      return (
                        <td key={side} className="merge-value">
                          <label className={chosen === side ? 'merge-pick chosen' : 'merge-pick'}>
                            <input
                              type="radio"
                              name={`field-${row.name}`}
                              checked={chosen === side}
                              onChange={() =>
                                setPicks((current) => ({ ...current, [row.name]: side }))
                              }
                            />
                            {value || <em className="muted">empty</em>}
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2 className="section-heading">3. Confirm</h2>
        <ul className="merge-summary">
          <li>
            <span className="mono">{survivor}</span> is kept, with{' '}
            {survivorHistory.total}
            {survivorHistory.approximate ? '+' : ''} document
            {survivorHistory.total === 1 ? '' : 's'}.
          </li>
          <li>
            {carried.length === 0 ? (
              <>It keeps all of its own field values.</>
            ) : (
              <>
                It takes {carried.length} value{carried.length === 1 ? '' : 's'} from{' '}
                <span className="mono">{loser}</span>:{' '}
                {carried.map((r) => fieldLabel(r.name)).join(', ')}.
              </>
            )}
          </li>
          <li>
            <span className="mono">{loser}</span> is set to inactive
            {loserHistory.total > 0 && (
              <>
                {' '}
                — its {loserHistory.total}
                {loserHistory.approximate ? '+' : ''} document
                {loserHistory.total === 1 ? '' : 's'} stay under it and are still findable
              </>
            )}
            .
          </li>
          <li>Both records get a note saying what happened and where the other one is.</li>
        </ul>

        {loserHistory.total > survivorHistory.total && (
          <div className="notice warn">
            <strong>The record you are retiring has more history.</strong>{' '}
            <span className="mono">{loser}</span> carries {loserHistory.total} document
            {loserHistory.total === 1 ? '' : 's'} against{' '}
            <span className="mono">{survivor}</span>&apos;s {survivorHistory.total}. That
            history cannot move, so it would sit under an inactive record. Check this is what
            you want.
          </div>
        )}

        <WriteBlockedNotice policy={policy} tenantId={tenantId!} place="edits" />

        <div className="form-actions">
          <Link className="button-link" to={`/tenants/${tenantId}/entities/${entity}/duplicates`}>
            Cancel
          </Link>
          <button
            type="button"
            className="primary"
            onClick={() => void merge()}
            // ⚠ The same gate as the grid. A merge is a write and the API
            // enforces writePolicy on it, but this screen never loaded the
            // policy — so an unpaid or support-stopped customer only found out
            // at submit, with no billing link and no explanation. A policy that
            // failed to load blocks too; null is unknown, not allowed.
            disabled={submitting || !canConfirm({ policy, changeCount: null })}
          >
            {submitting
              ? progress && progress.total > 0
                ? `Merging… ${progress.done} of ${progress.total}`
                : 'Merging…'
              : `Keep ${survivor}, retire ${loser}`}
          </button>
        </div>
        <p className="muted small">
          Both records are re-read immediately before anything is written, and every change is
          recorded. If this turns out to be wrong, undo it from job history.
        </p>
      </div>
    </>
  );
}
